import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import { deleteAddress, fetchAddress, fetchAddresses, patchAddress, postAddress } from '@sophocles/api/address';
import { deleteContact, fetchContact, fetchContacts, patchContact, postContact } from '@sophocles/api/contact';
import { fetchOrderSales } from '@sophocles/api/orderSale';
import {
  deleteOrderSaleItem,
  fetchOrderSaleItem,
  fetchOrderSaleItems,
  patchOrderSaleItem,
  postOrderSaleItem,
} from '@sophocles/api/orderSaleItem';
import {
  deleteShippingType,
  fetchShippingType,
  fetchShippingTypes,
  patchShippingType,
  postShippingType,
} from '@sophocles/api/shippingType';
import { createCrudThunks } from '@sophocles/features/warehouse/crudThunks';
import {
  AddressSchema,
  ContactSchema,
  OrderSaleItemSchema,
  ShippingTypeSchema,
} from '@sophocles/features/warehouse/orders/schema';

// OrderSale Thunks
const orderSaleThunks = createCrudThunks('warehouse/orderSales', { fetchMany: fetchOrderSales });
export const loadOrderSales = orderSaleThunks.loadMany;

// OrderSale single-entity reads/writes (fetchOne/create/patch/delete) migrated
// to RTK Query — see features/entities/orderSales.js. The list loader stays.

// OrderSaleItem Thunks
const orderSaleItemThunks = createCrudThunks(
  'warehouse/orderSaleItems',
  {
    fetchMany: fetchOrderSaleItems,
    fetchOne: fetchOrderSaleItem,
    create: postOrderSaleItem,
    patch: patchOrderSaleItem,
    remove: deleteOrderSaleItem,
  },
  data => normalize(data, OrderSaleItemSchema),
);
export const loadOrderSaleItems = orderSaleItemThunks.loadMany;
export const loadOrderSaleItem = orderSaleItemThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so
// orderSaleItemSlice's createEntitySlice extraReducers compile — no UI dispatches them.
export const createOrderSaleItem = orderSaleItemThunks.create;
export const editOrderSaleItem = orderSaleItemThunks.edit;
export const removeOrderSaleItem = orderSaleItemThunks.remove;

// Contact Thunks
const contactThunks = createCrudThunks(
  'warehouse/contacts',
  {
    fetchMany: fetchContacts,
    fetchOne: fetchContact,
    create: postContact,
    patch: patchContact,
    remove: deleteContact,
  },
  data => normalize(data, ContactSchema),
);
export const loadContacts = contactThunks.loadMany;
export const loadContact = contactThunks.loadOne;
export const createContact = contactThunks.create;
export const editContact = contactThunks.edit;
// DEPRECATED(grid-legacy-cleanup): kept only so contactSlice's createEntitySlice
// extraReducers compile — no UI dispatches this remove.
export const removeContact = contactThunks.remove;

// Address Thunks — no normalizr shape; the endpoint's raw response is used as-is.
const addressThunks = createCrudThunks('warehouse/addresses', {
  fetchMany: fetchAddresses,
  fetchOne: fetchAddress,
  create: postAddress,
  patch: patchAddress,
  remove: deleteAddress,
});
export const loadAddresses = addressThunks.loadMany;
export const loadAddress = addressThunks.loadOne;
export const createAddress = addressThunks.create;
export const editAddress = addressThunks.edit;
// DEPRECATED(grid-legacy-cleanup): kept only so addressSlice's createEntitySlice
// extraReducers compile — no UI dispatches this remove.
export const removeAddress = addressThunks.remove;

// ShippingType Thunks — fetchOne does not normalize (unlike create/patch below); kept
// hand-written rather than folded into the pre-existing inconsistency.
const shippingTypeThunks = createCrudThunks(
  'warehouse/shippingTypes',
  {
    fetchMany: fetchShippingTypes,
    create: postShippingType,
    patch: patchShippingType,
    remove: deleteShippingType,
  },
  data => normalize(data, ShippingTypeSchema),
);
export const loadShippingTypes = shippingTypeThunks.loadMany;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so
// shippingTypeSlice's createEntitySlice extraReducers compile — no UI dispatches them.
export const createShippingType = shippingTypeThunks.create;
export const editShippingType = shippingTypeThunks.edit;
export const removeShippingType = shippingTypeThunks.remove;

export const loadShippingType = createAsyncThunk(
  'warehouse/shippingTypes/fetchOne',
  async (shippingTypeId, { rejectWithValue }) => {
    try {
      const data = await fetchShippingType(shippingTypeId);
      return data;
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);
